/* eslint-disable max-lines */
import { Dispatch, forwardRef, useCallback, useState } from 'react';
import { SearchSuggestionTabType } from '../../../../AnalyticsContext/LinkTracking';
import { useSuggestionHandler } from '../../useSuggestionHandler';
import { EmptySearchSuggestionsSearchBar } from '../EmptySearchSuggestionsSearchBar/EmptySearchSuggestionsSearchBar';
import { SuggestionData } from '../types';
import { PracticeSuggestions } from './PracticeSuggestions/PracticeSuggestions';
import { PractitionerSuggestions } from './PractitionerSuggestions/PractitionerSuggestions';
import { SingleViewSearchCategory } from './SingleViewSearchCategory';
import { SpecialtySuggestions } from './SpecialtySuggestions/SpecialtySuggestions';
import {
  SuggestionLabel,
  SearchSuggestionCategory,
  PluraliseSearchSuggestionCategory,
} from './SuggestionLabel';
import { SuggestionList } from './SuggestionList/SuggestionList';
import { SuggestionsContainer } from './SuggestionsContainer/SuggestionsContainer';
import { SingleSuggestionsLayoutTemplate } from './SuggestionsLayoutTemplate/SingleSuggestionsLayoutTemplate';
import { SuggestionsLayoutTemplate } from './SuggestionsLayoutTemplate/SuggestionsLayoutTemplate';
import { TopicSuggestions } from './TopicSuggestions/TopicSuggestions';
import { useTextSuggestion } from './useTextSuggestion';

interface Props {
  searchTerm: string;
  handleOnSpecialtySuggestionCallback: Dispatch<SuggestionData>;
  setValue: Setter<string>;
  inline?: boolean;
  disableTabs?: boolean;
  enabledFilterOffset?: boolean;
  setSearchResultsSingleViewIsShowing: Setter<boolean>;
  searchResultsSingleViewIsShowing: boolean;
  setLocationFocused?: Setter<boolean>;
  locationValue?: string;
  setSpecialtyFocused?: Setter<boolean>;
  setShowPlaceholder: Setter<boolean>;
  setExpanded?: Setter<boolean>;
}

const SuggestionTypeToDisplay: Record<
  SuggestionLabel,
  typeof PracticeSuggestions | typeof PractitionerSuggestions
> = {
  Specialties: SpecialtySuggestions,
  Practices: PracticeSuggestions,
  Practitioners: PractitionerSuggestions,
  Topics: TopicSuggestions,
};

const mapTabToTrackingValue: Record<SuggestionLabel, SearchSuggestionTabType> = {
  Specialties: SearchSuggestionTabType.SPECIALTY,
  Practices: SearchSuggestionTabType.PRACTICE,
  Practitioners: SearchSuggestionTabType.PRACTITIONER,
  Topics: SearchSuggestionTabType.Topics,
};

const mapToSuggestionType: Record<SuggestionLabel, SearchSuggestionCategory> = {
  Specialties: SearchSuggestionCategory.SPECIALTY,
  Practices: SearchSuggestionCategory.PRACTICE,
  Practitioners: SearchSuggestionCategory.PRACTITIONER,
  Topics: SearchSuggestionCategory.TOPIC,
};

const mapToPluraliseSuggestionType: Record<SuggestionLabel, PluraliseSearchSuggestionCategory> = {
  Specialties: PluraliseSearchSuggestionCategory.SPECIALTY,
  Practices: PluraliseSearchSuggestionCategory.PRACTICE,
  Practitioners: PluraliseSearchSuggestionCategory.PRACTITIONER,
  Topics: PluraliseSearchSuggestionCategory.TOPIC,
};

export const NewSuggestions = forwardRef<HTMLDivElement, Props>(
  (
    {
      searchResultsSingleViewIsShowing,
      setSearchResultsSingleViewIsShowing,
      searchTerm,
      handleOnSpecialtySuggestionCallback,
      setValue,
      inline = false,
      enabledFilterOffset,
      setShowPlaceholder,
    },
    ref,
  ) => {
    const [showErrorText, setShowErrorText] = useState(false);
    const [currentListIndex, setCurrentListIndex] = useState(0);
    const textSearchSuggestion: SuggestionData[] = useTextSuggestion(searchTerm);
    const [selectedSuggestionType, setSuggetionViewType] = useState<SuggestionLabel>('Specialties');

    const runSearchFromSuggestion = useSuggestionHandler();

    const ListComponent = SuggestionTypeToDisplay[selectedSuggestionType];

    const toggleSingleSuggestionView = (viewType: SingleViewSearchCategory) => {
      setSearchResultsSingleViewIsShowing(viewType.showSingleView);

      if (viewType.category) {
        setSuggetionViewType(viewType.category);
      }
    };

    const handleOnSpecialtySuggestion = useCallback(
      (suggestion: SuggestionData, index: number) => {
        const isEmptySuggestions = index === -1;
        const isTextSearch = index === 50;
        const isCurrentlyFocusedIndex = index.toString()[0] === currentListIndex.toString();

        if (currentListIndex === 0 && !isEmptySuggestions && !isTextSearch) {
          setShowErrorText(true);
          return;
        } else {
          setShowErrorText(false);
        }

        if (
          !searchResultsSingleViewIsShowing &&
          !isCurrentlyFocusedIndex &&
          !isEmptySuggestions &&
          !isTextSearch
        ) {
          return;
        }

        if (suggestion.type === 'PracticeSuggestion') {
          setValue(suggestion.practice.name);

          runSearchFromSuggestion(suggestion);
        }

        if (suggestion.type === 'PractitionerSuggestion') {
          setValue(suggestion.practitioner.firstName + ' ' + suggestion.practitioner.lastName);

          runSearchFromSuggestion(suggestion);
        }

        if (suggestion.type === 'FilterSuggestion') {
          const suggText =
            suggestion.filters.topic?.treatment ?? suggestion.filters.topic?.specialist;

          setValue(suggText ?? '');
        }

        if (suggestion.type === 'TextSuggestion') {
          setValue(suggestion.filters.text);
        }

        setShowPlaceholder(true);
        setSearchResultsSingleViewIsShowing(false);
        handleOnSpecialtySuggestionCallback(suggestion);
      },
      [
        currentListIndex,
        searchResultsSingleViewIsShowing,
        setShowPlaceholder,
        setSearchResultsSingleViewIsShowing,
        handleOnSpecialtySuggestionCallback,
        setValue,
        runSearchFromSuggestion,
      ],
    );

    const searchFooter = (
      <SuggestionList
        suggestions={textSearchSuggestion}
        onChoose={handleOnSpecialtySuggestion}
        trackingData={{ searchSuggestionTab: SearchSuggestionTabType.ALL }}
        searchTerm={searchTerm}
        toggleSingleSuggestionView={toggleSingleSuggestionView}
        indexIdentifier={5}
      />
    );

    const emptySuggestionClickHandler = (specialist: string) => {
      const specialtySuggestion: SuggestionData = {
        type: 'FilterSuggestion',
        id: `specialty-${specialist}`,
        filters: { topic: { specialist: specialist } },
      };

      handleOnSpecialtySuggestion(specialtySuggestion, -1);
    };

    return (
      <SuggestionsContainer ref={ref} enabledFilterOffset={enabledFilterOffset} inline={inline}>
        {!searchTerm && !inline && (
          <EmptySearchSuggestionsSearchBar setValue={emptySuggestionClickHandler} />
        )}
        {searchTerm && !searchResultsSingleViewIsShowing && (
          <SuggestionsLayoutTemplate
            error={showErrorText}
            serviceList={
              <SpecialtySuggestions
                searchTerm={searchTerm}
                onChoose={handleOnSpecialtySuggestion}
                trackingData={{ searchSuggestionTab: SearchSuggestionTabType.SPECIALTY }}
                toggleSingleSuggestionView={toggleSingleSuggestionView}
                showSingleView={searchResultsSingleViewIsShowing}
                onFocus={setCurrentListIndex}
                listIndex={1}
              />
            }
            practiceList={
              <PracticeSuggestions
                searchTerm={searchTerm}
                onChoose={handleOnSpecialtySuggestion}
                trackingData={{ searchSuggestionTab: SearchSuggestionTabType.PRACTICE }}
                toggleSingleSuggestionView={toggleSingleSuggestionView}
                showSingleView={searchResultsSingleViewIsShowing}
                onFocus={setCurrentListIndex}
                listIndex={2}
              />
            }
            practitionerList={
              <PractitionerSuggestions
                searchTerm={searchTerm}
                onChoose={handleOnSpecialtySuggestion}
                trackingData={{ searchSuggestionTab: SearchSuggestionTabType.PRACTITIONER }}
                toggleSingleSuggestionView={toggleSingleSuggestionView}
                showSingleView={searchResultsSingleViewIsShowing}
                onFocus={setCurrentListIndex}
                listIndex={3}
              />
            }
            interestProcedureList={
              <TopicSuggestions
                searchTerm={searchTerm}
                onChoose={handleOnSpecialtySuggestion}
                trackingData={{ searchSuggestionTab: SearchSuggestionTabType.Topics }}
                toggleSingleSuggestionView={toggleSingleSuggestionView}
                showSingleView={searchResultsSingleViewIsShowing}
                onFocus={setCurrentListIndex}
                listIndex={4}
              />
            }
          />
        )}
        {searchTerm && searchResultsSingleViewIsShowing && (
          <SingleSuggestionsLayoutTemplate
            suggestionList={
              <ListComponent
                searchTerm={searchTerm}
                onChoose={handleOnSpecialtySuggestion}
                trackingData={{
                  searchSuggestionTab: mapTabToTrackingValue[selectedSuggestionType],
                }}
                toggleSingleSuggestionView={toggleSingleSuggestionView}
                showSingleView={searchResultsSingleViewIsShowing}
                onFocus={setCurrentListIndex}
                listIndex={6}
              />
            }
            suggestionType={mapToSuggestionType[selectedSuggestionType]}
            pluraliseType={mapToPluraliseSuggestionType[selectedSuggestionType]}
            toggleSingleSuggestionView={toggleSingleSuggestionView}
            trackingData={{ searchSuggestionTab: mapTabToTrackingValue[selectedSuggestionType] }}
            footer={searchFooter}
          />
        )}

        {searchTerm && searchFooter}
      </SuggestionsContainer>
    );
  },
);
